import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiSearch, FiFilter, FiEye, FiEdit, FiTrash2, FiMail, FiPhone } from 'react-icons/fi';
import { formatRandSimple, formatRandNoDecimals } from '../../utils/currency';
import styles from './ClientList.module.css';

interface Client {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  status: 'active' | 'inactive';
  totalRevenue: number;
  invoiceCount: number;
  lastInvoiceDate: string;
  tags: string[];
}

// Mock data
const mockClients: Client[] = [
  {
    id: '1',
    name: 'John Smith',
    email: 'john.smith@acme.com',
    phone: '+1 (555) 123-4567',
    company: 'Acme Corporation',
    status: 'active',
    totalRevenue: 45000,
    invoiceCount: 12,
    lastInvoiceDate: '2024-01-15',
    tags: ['VIP', 'Enterprise']
  },
  {
    id: '2',
    name: 'Sarah Johnson',
    email: 'sarah.j@techsolutions.com',
    phone: '+1 (555) 234-5678',
    company: 'Tech Solutions Inc',
    status: 'active',
    totalRevenue: 38000,
    invoiceCount: 8,
    lastInvoiceDate: '2024-01-20',
    tags: ['SMB']
  },
  {
    id: '3',
    name: 'Michael Brown',
    email: 'michael.b@globalindustries.com',
    phone: '+1 (555) 345-6789',
    company: 'Global Industries',
    status: 'active',
    totalRevenue: 32000,
    invoiceCount: 6,
    lastInvoiceDate: '2024-01-10',
    tags: ['Enterprise']
  },
  {
    id: '4',
    name: 'Emily Davis',
    email: 'emily.d@innovationlabs.com',
    phone: '+1 (555) 456-7890',
    company: 'Innovation Labs',
    status: 'inactive',
    totalRevenue: 28000,
    invoiceCount: 5,
    lastInvoiceDate: '2023-12-15',
    tags: ['Startup']
  },
  {
    id: '5',
    name: 'Segodi Hutamo',
    email: 'segodi@hutstech.co.za',
    phone: '+27 82 826 8596',
    company: 'HutsTech',
    status: 'active',
    totalRevenue: 0,
    invoiceCount: 0,
    lastInvoiceDate: '',
    tags: ['Tech', 'South Africa']
  }
];

export function ClientList() {
  const [clients, setClients] = useState<Client[]>(mockClients);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedClients, setSelectedClients] = useState<string[]>([]);

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.company.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || client.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      setClients(clients.filter(client => client.id !== id));
    }
  };

  const handleBulkDelete = () => {
    if (selectedClients.length === 0) return;
    if (window.confirm(`Are you sure you want to delete ${selectedClients.length} client(s)?`)) {
      setClients(clients.filter(client => !selectedClients.includes(client.id)));
      setSelectedClients([]);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedClients(filteredClients.map(client => client.id));
    } else {
      setSelectedClients([]);
    }
  };

  const handleSelectClient = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedClients([...selectedClients, id]);
    } else {
      setSelectedClients(selectedClients.filter(clientId => clientId !== id));
    }
  };

  const getStatusBadge = (status: string) => {
    return status === 'active' ? styles.statusActive : styles.statusInactive;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Clients</h1>
          <p className={styles.subtitle}>Manage your client relationships and contact information</p>
        </div>
        <Link to="/clients/create" className={styles.createButton}>
          <FiPlus size={16} />
          New Client
        </Link>
      </div>

      {/* Filters and Search */}
      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <FiSearch className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        
        <div className={styles.filterGroup}>
          <FiFilter className={styles.filterIcon} />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={styles.statusFilter}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedClients.length > 0 && (
        <div className={styles.bulkActions}>
          <span>{selectedClients.length} client(s) selected</span>
          <button onClick={handleBulkDelete} className={styles.bulkDeleteButton}>
            <FiTrash2 size={16} />
            Delete Selected
          </button>
        </div>
      )}

      {/* Clients Grid */}
      <div className={styles.clientsGrid}>
        {filteredClients.map((client) => (
          <div key={client.id} className={styles.clientCard}>
            <div className={styles.cardHeader}>
              <div className={styles.clientInfo}>
                <h3 className={styles.clientName}>{client.name}</h3>
                <p className={styles.clientCompany}>{client.company}</p>
                <span className={`${styles.statusBadge} ${getStatusBadge(client.status)}`}>
                  {client.status.charAt(0).toUpperCase() + client.status.slice(1)}
                </span>
              </div>
              <div className={styles.cardActions}>
                <input
                  type="checkbox"
                  checked={selectedClients.includes(client.id)}
                  onChange={(e) => handleSelectClient(client.id, e.target.checked)}
                  className={styles.checkbox}
                />
                <Link to={`/clients/${client.id}`} className={styles.actionButton} title="View">
                  <FiEye size={16} />
                </Link>
                <Link to={`/clients/${client.id}/edit`} className={styles.actionButton} title="Edit">
                  <FiEdit size={16} />
                </Link>
                <button 
                  onClick={() => handleDelete(client.id)} 
                  className={`${styles.actionButton} ${styles.deleteButton}`}
                  title="Delete"
                >
                  <FiTrash2 size={16} />
                </button>
              </div>
            </div>

            <div className={styles.contactInfo}>
              <div className={styles.contactItem}>
                <FiMail className={styles.contactIcon} />
                <span>{client.email}</span>
              </div>
              <div className={styles.contactItem}>
                <FiPhone className={styles.contactIcon} />
                <span>{client.phone}</span>
              </div>
            </div>

            <div className={styles.clientStats}>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Total Revenue</span>
                <span className={styles.statValue}>{formatRandNoDecimals(client.totalRevenue)}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Invoices</span>
                <span className={styles.statValue}>{client.invoiceCount}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Last Invoice</span>
                <span className={styles.statValue}>
                  {new Date(client.lastInvoiceDate).toLocaleDateString()}
                </span>
              </div>
            </div>

            {client.tags.length > 0 && (
              <div className={styles.tags}>
                {client.tags.map((tag, index) => (
                  <span key={index} className={styles.tag}>
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredClients.length === 0 && (
        <div className={styles.emptyState}>
          <p>No clients found. Add your first client to get started.</p>
          <Link to="/clients/create" className={styles.createButton}>
            <FiPlus size={16} />
            Add Client
          </Link>
        </div>
      )}
    </div>
  );
}
